// Champ de saisie tokenisé, compatible react-hook-form (forwardRef).
// Label + hint + erreur, focus visible, état erreur.

import { forwardRef, useId, type InputHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';
import { fontStyle } from '@/theme/typography';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label?: string;
  hint?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label htmlFor={inputId} style={fontStyle('bodySmMedium')} className="text-text-secondary">
          {label}
        </label>
      ) : null}
      <input
        {...rest}
        id={inputId}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        style={fontStyle('body')}
        className={cn(
          'h-12 rounded-xl border bg-surface px-3.5 text-text-primary outline-none',
          'transition-[color,border-color,box-shadow] duration-200',
          'placeholder:text-text-tertiary',
          error
            ? 'border-danger focus:shadow-[0_0_0_4px_color-mix(in_srgb,var(--rmbr-danger)_14%,transparent)]'
            : 'border-border hover:border-text-tertiary focus:border-action focus:shadow-[0_0_0_4px_color-mix(in_srgb,var(--rmbr-action)_14%,transparent)]',
        )}
      />
      {error ? (
        <span id={`${inputId}-error`} style={fontStyle('bodySm')} className="text-danger">
          {error}
        </span>
      ) : hint ? (
        <span id={`${inputId}-hint`} style={fontStyle('bodySm')} className="text-text-tertiary">
          {hint}
        </span>
      ) : null}
    </div>
  );
});
